import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { PlayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { ModalPortalShell } from '../ui/ModalPortalShell';
import { CodedStatusBadge } from '../ui/CodedStatusBadge';
import {
  MyTaskListItem,
  addMyTaskEvidence,
  deleteMyTaskEvidence,
  duplicateMyTask,
  fetchMyTaskEvidenceImageUrl,
  listMyTaskEvidences,
  replaceMyTaskAreas,
  updateMyTaskEvidence,
} from '../../services/myTasksApi';
import {
  PlanningWorkItemEvidence,
  PlanningWorkItemEvidenceFormPayload,
} from '../../services/strategicPlanningApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { PlanningTaskEvidenceSection } from '../strategic-planning/PlanningTaskEvidenceSection';
import { TaskAreaPicker } from '../strategic-planning/TaskAreaPicker';
import { CollaboratorAutocomplete } from '../ui/CollaboratorAutocomplete';
import { RequiredMark } from '../ui/RequiredMark';
import {
  PLANNING_EVALUATION_RESULT_OPTIONS,
  PLANNING_FULFILLMENT_OPTIONS,
  formatPlanningDeliverableResult,
  formatPlanningEvaluationDeadline,
  formatPlanningEvaluationImpact,
  formatPlanningEvaluationQuality,
  formatPlanningFulfillmentResult,
  formatPlanningTaskStatusLabel,
} from '../../types/PlanningTaskStatus';

type EvaluationFormState = {
  result: string;
  quality: string;
  deadline: string;
  impact: string;
  lessonsLearned: string;
  notes: string;
};

type MyTaskDetailModalProps = {
  task: MyTaskListItem;
  saving: boolean;
  fulfillmentResult: string;
  evaluationForm: EvaluationFormState;
  onClose: () => void;
  onFulfillmentChange: (value: string) => void;
  onEvaluationChange: (patch: Partial<EvaluationFormState>) => void;
  onAssign: (ownerUserId: string) => void;
  onStart: () => void;
  onPause: () => void;
  onExtendDeadline: () => void;
  onCancel: () => void;
  onFinish: () => void;
  onEvaluate: (event: FormEvent) => void;
  onDuplicated: () => void;
  onAreasChange: (areas: string[]) => void;
};

function kindEyebrow(kind?: string, focusedEvaluation?: boolean): string {
  switch (kind) {
    case 'Meta':
      return focusedEvaluation ? 'Avaliação de meta' : 'Detalhes da meta';
    case 'Marco':
      return focusedEvaluation ? 'Avaliação de marco' : 'Detalhes do marco';
    default:
      return 'Detalhes da tarefa';
  }
}

function parentLinkLabel(task: MyTaskListItem): string | null {
  if (task.kind === 'Marco' && task.parentWorkItemTitle) {
    return `Meta: ${task.parentWorkItemTitle}`;
  }

  if (task.kind === 'Meta' && task.objectiveTitle) {
    return `Objetivo: ${task.objectiveTitle}`;
  }

  return null;
}

function cancelLabel(kind?: string): string {
  switch (kind) {
    case 'Meta':
      return 'Cancelar meta';
    case 'Marco':
      return 'Cancelar marco';
    default:
      return 'Cancelar tarefa';
  }
}

export function MyTaskDetailModal({
  task,
  saving,
  fulfillmentResult,
  evaluationForm,
  onClose,
  onFulfillmentChange,
  onEvaluationChange,
  onAssign,
  onStart,
  onPause,
  onExtendDeadline,
  onCancel,
  onFinish,
  onEvaluate,
  onDuplicated,
  onAreasChange,
}: MyTaskDetailModalProps) {
  const isGoalItem = task.kind === 'Meta' || task.kind === 'Marco';
  const showFinishBlock = Boolean(task.canFinish);
  const showEvaluateBlock = Boolean(task.canEvaluate);
  const isFocusedEvaluation = isGoalItem && (showFinishBlock || showEvaluateBlock);
  const parentLink = parentLinkLabel(task);
  const [evidences, setEvidences] = useState<PlanningWorkItemEvidence[]>([]);
  const [evidenceBusy, setEvidenceBusy] = useState(false);
  const evidenceLocked = saving || evidenceBusy;

  useEffect(() => {
    let cancelled = false;
    setEvidences([]);
    void listMyTaskEvidences(task.id)
      .then((items) => {
        if (!cancelled) setEvidences(items);
      })
      .catch(() => {
        if (!cancelled) setEvidences([]);
      });
    return () => {
      cancelled = true;
    };
  }, [task.id]);

  async function reloadEvidences(): Promise<void> {
    setEvidences(await listMyTaskEvidences(task.id));
  }

  async function handleAddEvidence(payload: PlanningWorkItemEvidenceFormPayload): Promise<void> {
    setEvidenceBusy(true);
    try {
      await addMyTaskEvidence(task.id, payload);
      toast.success('Evidência adicionada.');
      await reloadEvidences();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível adicionar evidência.'));
      throw error;
    } finally {
      setEvidenceBusy(false);
    }
  }

  async function handleUpdateEvidence(
    evidenceId: string,
    payload: PlanningWorkItemEvidenceFormPayload,
  ): Promise<void> {
    setEvidenceBusy(true);
    try {
      await updateMyTaskEvidence(task.id, evidenceId, payload);
      toast.success('Evidência atualizada.');
      await reloadEvidences();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível atualizar a evidência.'));
      throw error;
    } finally {
      setEvidenceBusy(false);
    }
  }

  async function handleDeleteEvidence(evidenceId: string): Promise<void> {
    setEvidenceBusy(true);
    try {
      await deleteMyTaskEvidence(task.id, evidenceId);
      toast.success('Evidência excluída.');
      await reloadEvidences();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível excluir a evidência.'));
      throw error;
    } finally {
      setEvidenceBusy(false);
    }
  }

  async function handleAreasChange(areas: string[]): Promise<void> {
    try {
      await replaceMyTaskAreas(task.id, areas);
      toast.success('Áreas da tarefa atualizadas.');
      onAreasChange(areas);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível atualizar as áreas da tarefa.'));
    }
  }

  async function handleDuplicate(): Promise<void> {
    try {
      await duplicateMyTask(task.id);
      toast.success('Tarefa duplicada sem responsável.');
      onDuplicated();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Não foi possível duplicar a tarefa.'));
    }
  }

  const footerActions: ReactNode = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {task.canCancel ? (
        <button
          type="button"
          className="rounded-lg border border-rose-300 bg-white px-3 py-2 text-sm font-medium text-rose-700 transition hover:bg-rose-50 disabled:opacity-60"
          disabled={saving}
          onClick={onCancel}
        >
          {cancelLabel(task.kind)}
        </button>
      ) : null}
      {!isGoalItem ? (
        <button
          type="button"
          className="erp-btn-secondary"
          disabled={saving}
          onClick={() => void handleDuplicate()}
        >
          Duplicar tarefa
        </button>
      ) : null}
      {showFinishBlock ? (
        <button
          type="button"
          className="erp-btn-primary"
          disabled={saving}
          onClick={onFinish}
        >
          {isGoalItem ? 'Concluir avaliação' : 'Concluir'}
        </button>
      ) : null}
      {showEvaluateBlock && !showFinishBlock ? (
        <button type="submit" form="task-evaluate-form" className="erp-btn-primary" disabled={saving}>
          Concluir avaliação
        </button>
      ) : null}
      <button
        type="button"
        className="rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm font-medium text-secondary-800 transition hover:bg-secondary-50"
        onClick={onClose}
      >
        Fechar
      </button>
    </div>
  );

  return (
    <ModalPortalShell
      open
      panelClassName="max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl"
      overlayClassName="z-[100]"
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <header className="shrink-0 border-b border-secondary-100 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-600/80">
                {kindEyebrow(task.kind, isFocusedEvaluation)}
              </p>
              <h2 className="mt-1 text-xl font-semibold tracking-tight text-secondary-900">
                {task.title}
              </h2>
              {parentLink ? (
                <p className="mt-1 text-sm text-primary-700/80">{parentLink}</p>
              ) : null}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-secondary-600">
                <span>Responsável: {task.ownerUserName || 'Não atribuído'}</span>
                <CodedStatusBadge
                  status={task.status}
                  label={formatPlanningTaskStatusLabel(task.status)}
                />
              </div>
            </div>
            <button
              type="button"
              className="rounded-lg p-1.5 text-secondary-400 transition hover:bg-secondary-100 hover:text-secondary-700"
              aria-label="Fechar"
              onClick={onClose}
            >
              <XMarkIcon className="h-5 w-5" aria-hidden />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {isGoalItem && (task.description || task.descriptionPreview) ? (
            <section>
              <h3 className="text-base font-semibold text-secondary-900">Descrição</h3>
              <p className="mt-1 text-sm leading-relaxed text-secondary-700">
                {task.description || task.descriptionPreview}
              </p>
            </section>
          ) : null}

          {!isGoalItem && task.descriptionPreview && !isFocusedEvaluation ? (
            <p className="text-sm leading-relaxed text-secondary-700">{task.descriptionPreview}</p>
          ) : null}

          {task.fulfillmentResult ? (
            <div className="rounded-lg border border-secondary-200/80 bg-secondary-50/70 px-3 py-2.5 text-sm text-secondary-800">
              Execução:{' '}
              <strong>{formatPlanningFulfillmentResult(task.fulfillmentResult)}</strong>
            </div>
          ) : null}

          {!isGoalItem ? (
            <TaskAreaPicker
              selectedIds={task.areas ?? []}
              disabled={saving}
              onChange={(areas) => {
                void handleAreasChange(areas);
              }}
            />
          ) : null}

          {task.canAssign ? (
            <div
              className={`space-y-2 rounded-xl border p-3 ${
                task.ownerUserId ? 'border-secondary-200' : 'border-amber-300 bg-amber-50/70'
              }`}
            >
              <p className="text-sm font-semibold text-secondary-800">
                Responsável <RequiredMark />
              </p>
              <p className="text-xs text-secondary-600">
                Clique no campo, busque pelo nome e clique fora para salvar.
              </p>
              <CollaboratorAutocomplete
                valueUserId={task.ownerUserId}
                valueUserName={task.ownerUserName}
                disabled={saving}
                saving={saving}
                onCommit={(user) => {
                  if (!user || user.userId === task.ownerUserId) return;
                  onAssign(user.userId);
                }}
              />
              {!task.ownerUserId ? (
                <p className="text-xs text-amber-800">
                  Atribua um responsável para poder iniciar a execução.
                </p>
              ) : null}
            </div>
          ) : null}

          {!isFocusedEvaluation ? (
            <div className="flex flex-wrap gap-2">
              {task.canStart ? (
                <button
                  type="button"
                  className="erp-btn-primary gap-2 text-sm"
                  disabled={saving}
                  onClick={onStart}
                >
                  <PlayIcon className="h-5 w-5" />
                  Iniciar
                </button>
              ) : null}
              {task.canPause ? (
                <button
                  type="button"
                  className="erp-btn-secondary text-sm"
                  disabled={saving}
                  onClick={onPause}
                >
                  Pausar
                </button>
              ) : null}
              {task.canExtendDeadline ? (
                <button
                  type="button"
                  className="erp-btn-secondary text-sm"
                  disabled={saving}
                  onClick={onExtendDeadline}
                >
                  Estender prazo
                </button>
              ) : null}
            </div>
          ) : null}

          {showFinishBlock ? (
            <section className="space-y-3">
              <div>
                <h3 className="text-base font-semibold text-secondary-900">Resultado da execução</h3>
                <p className="mt-1 text-sm text-secondary-500">
                  Informe como foi a execução dest{task.kind === 'Marco' ? 'e marco' : task.kind === 'Meta' ? 'a meta' : 'a tarefa'}.
                </p>
              </div>
              <select
                className="erp-input"
                value={fulfillmentResult}
                onChange={(event) => onFulfillmentChange(event.target.value)}
              >
                {PLANNING_FULFILLMENT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </section>
          ) : null}

          {showEvaluateBlock ? (
            <form
              id="task-evaluate-form"
              onSubmit={onEvaluate}
              className="space-y-3 rounded-xl border border-primary-200/70 bg-primary-50/60 p-4"
            >
              <div>
                <h3 className="text-sm font-semibold text-secondary-900">
                  Avaliar {task.kind === 'Meta' ? 'meta' : task.kind === 'Marco' ? 'marco' : 'tarefa'}
                </h3>
                <p className="mt-0.5 text-xs text-secondary-500">
                  Registre o resultado qualitativo da entrega.
                </p>
              </div>
              <label className="erp-label">
                Resultado
                <select
                  className="erp-input mt-1"
                  value={evaluationForm.result}
                  onChange={(event) => onEvaluationChange({ result: event.target.value })}
                >
                  {PLANNING_EVALUATION_RESULT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="erp-label">
                  Qualidade
                  <select
                    className="erp-input mt-1"
                    value={evaluationForm.quality}
                    onChange={(event) => onEvaluationChange({ quality: event.target.value })}
                  >
                    <option value="Excellent">Excelente</option>
                    <option value="Good">Boa</option>
                    <option value="Fair">Razoável</option>
                    <option value="Unsatisfactory">Insatisfatória</option>
                  </select>
                </label>
                <label className="erp-label">
                  Prazo
                  <select
                    className="erp-input mt-1"
                    value={evaluationForm.deadline}
                    onChange={(event) => onEvaluationChange({ deadline: event.target.value })}
                  >
                    <option value="Early">Adiantada</option>
                    <option value="OnTime">No prazo</option>
                    <option value="Late">Atrasada</option>
                  </select>
                </label>
                <label className="erp-label">
                  Impacto
                  <select
                    className="erp-input mt-1"
                    value={evaluationForm.impact}
                    onChange={(event) => onEvaluationChange({ impact: event.target.value })}
                  >
                    <option value="Low">Baixo</option>
                    <option value="Medium">Médio</option>
                    <option value="High">Alto</option>
                    <option value="Strategic">Estratégico</option>
                  </select>
                </label>
              </div>
              <label className="erp-label">
                Lições aprendidas
                <textarea
                  className="erp-input mt-1 min-h-[72px]"
                  value={evaluationForm.lessonsLearned}
                  onChange={(event) => onEvaluationChange({ lessonsLearned: event.target.value })}
                />
              </label>
              <label className="erp-label">
                Observações
                <textarea
                  className="erp-input mt-1 min-h-[72px]"
                  value={evaluationForm.notes}
                  onChange={(event) => onEvaluationChange({ notes: event.target.value })}
                />
              </label>
            </form>
          ) : null}

          {!showEvaluateBlock && task.evaluation ? (
            <div className="rounded-xl border border-secondary-200/80 bg-secondary-50/70 px-4 py-3 text-sm">
              <p className="font-semibold text-secondary-900">Avaliação registrada</p>
              <p className="mt-1 text-secondary-700">
                Resultado: {formatPlanningDeliverableResult(task.evaluation.result)}
              </p>
              <p className="mt-0.5 text-secondary-600">
                {formatPlanningEvaluationQuality(task.evaluation.quality)} ·{' '}
                {formatPlanningEvaluationDeadline(task.evaluation.deadline)} ·{' '}
                {formatPlanningEvaluationImpact(task.evaluation.impact)}
              </p>
            </div>
          ) : null}

          <PlanningTaskEvidenceSection
            taskId={task.id}
            evidences={evidences}
            canWrite
            busy={evidenceLocked}
            loadImageUrl={fetchMyTaskEvidenceImageUrl}
            onAdd={handleAddEvidence}
            onUpdate={handleUpdateEvidence}
            onDelete={handleDeleteEvidence}
          />
        </div>

        <footer className="shrink-0 border-t border-secondary-100 bg-white px-5 py-4">
          {footerActions}
        </footer>
      </div>
    </ModalPortalShell>
  );
}
